import { isAfterDate } from '@superbet-group/web.lib.utilities';
import {
    Rule,
    SuperAdvantageEligibility,
} from '@superbet-group/web.types';
import {
    isEmpty as _isEmpty,
    get as _get,
    find as _find,
    includes as _includes,
    keyBy as _keyBy,
} from 'lodash';

import {
    MarketAttribute,
    competitorDelimiter,
    superLiveTag,
} from '@core/constants';
import Market from '@models/offer/Market';
import { MetaData } from '@models/offer/MetaData';
import Odd from '@models/offer/Odd';
import {
    EventCategory,
    OfferStatus,
    OfferType,
    VideoProviderType,
} from '@models/offer/enums';
import {
    createCache,
    Cache,
} from '@utils/cache/createCache';

import MatchResult, { Score } from './MatchResult';
import ISportlikeEvent from '../base/ISportlikeEvent';

function isSameSpecialBetValue(odd: Odd, specialBetValue: string | undefined): boolean {
    return odd.specialBetValue === specialBetValue
        || (_isEmpty(odd.specialBetValue) && _isEmpty(specialBetValue));
}

export function createMarket(odd: Odd): Market {
    return new Market({
        id: odd.marketId,
        originalName: odd.marketOriginalName,
        uniqueId: odd.getUniqueMarketId(),
        name: odd.getUniqueMarketName(),
        order: odd.marketOrder,
        tags: odd.tags,
        isLocked: odd.isLocked,
        odds: [odd],
        isSuperbet: odd.isSuperbet,
    });
}

type MarketCacheKey = number | string;
type MarketCacheValue = Market | null;

export default class OfferEvent implements ISportlikeEvent {
    id: number;
    uuid: string;
    offerId: number;
    fullName: string;
    sportId: number;
    sportName: string;
    tournamentId: number;
    tournamentName: string;
    tournamentOriginalName: string;
    date: Date;
    utcDate?: string;
    oddCount?: number;
    marketCount?: number;
    matchTags: string[];
    matchTimestamp: number;

    // NOTE: Out-rights don't have it
    team1Id?: number;
    team2Id?: number;

    // NOTE: Out-rights don't have it
    code?: number;

    marketsCache: Cache<MarketCacheKey, MarketCacheValue> = createCache<MarketCacheKey, MarketCacheValue>();

    matchResults: {
        isGoingToNextRound: null | 1 | 2;
        winner: null | 1 | 2;
        finalScore: null| Score;
        totalPoints: null | number;
        score: MatchResult[];
        result: null | Score;
    };

    tournamentCategoryName?: string;

    tournamentName2?: string;
    // every superbet change (on every Match field) is +1
    incrementId: number;
    categoryId: number;
    categoryName: string;
    sportOrder: number;
    categoryOrder?: number;
    tournamentOrder?: number;
    betRadarId: string | undefined;
    specialTournamentOrder?: number;
    isGoingToBeInLiveBetting?: boolean;
    offerStatus?: Record<OfferType, OfferStatus>;
    rules: Rule[] = [];
    metaData?: MetaData | undefined;
    hasStream: boolean;
    matchPromotedLiveOrder: number;
    tournamentPromotedLiveOrder?: number;

    streamInfo: RequireOnlyOne<{
        [VideoProviderType.BETRADAR]?: number;
        [VideoProviderType.IMG]?: number;
        [VideoProviderType.YOUTUBE]?: string;
        [VideoProviderType.TWITCH]?: string;
        [VideoProviderType.PERFORM]?: string;
        [VideoProviderType.BET_GENIUS]?: string;
        [VideoProviderType.INFRONTSPORTS]?: string;
        [VideoProviderType.BETER]?: string,
    }> | {
        [VideoProviderType.IMG]?: number;
        [VideoProviderType.IMG_VISUALIZATION_WIDGET]?: string;
    } | Record<string, never>; // never is {}

    matchFooter: string | null = null;

    tournamentFooter: string | null = null;
    private odds: Odd[] = [];
    oddsUpdatedAt = 0;

    // used when Match has finished
    oddResults: Odd[] = [];

    // event and odd
    validStruct?: boolean;

    eventCategory?: EventCategory;
    team1Name: string;
    team2Name: string;

    superAdvantageEligibility?: SuperAdvantageEligibility;

    private static isSpecifierPresent(odd: Odd): boolean {
        return !!odd.marketOriginalName && odd.marketOriginalName.includes('{');
    }

    public getMarketIds(): number[] {
        return this.getOdds()
            .filter((odd) => !OfferEvent.isSpecifierPresent(odd))
            .map((odd) => odd.marketId);
    }

    public getMarkets(): Market[] {
        const markets: Map<string, Market> = new Map();

        this.getOdds().forEach((odd) => {
            const marketKey = odd.getUniqueMarketName();

            let market = markets.get(marketKey);
            if (!market) {
                market = createMarket(odd);
                markets.set(marketKey, market);
            } else {
                market.odds.push(odd);
            }
            market.isLocked = market.isLocked && odd.isLocked;
        });

        const result = [...markets.values()];
        result.forEach((market) => {
            // eslint-disable-next-line no-param-reassign
            market.isLocked = market.odds.every((odd) => odd.isLocked);
        });
        return result;
    }

    public getPrematchMarket(prematchTag: string = MarketAttribute.main): Market | null {
        return this.marketsCache.get(prematchTag, () => {
            let isPrematchMarket;

            if (prematchTag === MarketAttribute.main) {
                isPrematchMarket = (odd: Odd) => odd.isMain();
            } else {
                isPrematchMarket = (odd: Odd) => odd.isMarketType(prematchTag);
            }

            return this.getMarket(isPrematchMarket);
        });
    }

    public getMarketById(marketId: number): Market | null {
        return this.marketsCache.get(marketId, () => {
            return this.getMarket((odd) => odd.marketId === marketId);
        });
    }

    private getMarket(predicate: (odd: Odd) => boolean): Market | null {
        return getMarketBy(
            this.getOdds(),
            predicate,
        );
    }

    public containsFullOddList(): boolean {
        return _get(this, 'oddCount', 0) === this.odds.length;
    }

    public useOddResults(): boolean {
        return this.isFinished() && this.oddResults.length > 0;
    }

    public getOdds(): Odd[] {
        return this.useOddResults() ? this.oddResults : this.odds;
    }

    public getOddById(id: number, specialBetValue?: string): Odd | undefined {
        return _find(this.getOdds(), (odd) =>
            odd.id === id
            && isSameSpecialBetValue(odd, specialBetValue),
        );
    }

    public getOddByUuid(uuid: string): Odd | undefined {
        return _find(this.getOdds(), (odd) => odd.uuid === uuid);
    }

    public getOdd({
        uuid,
        id,
        specialBetValue,
    }: {
        uuid?: string;
        id: number;
        specialBetValue?: string;
    }): Odd | undefined {
        if (uuid) {
            return this.getOddByUuid(uuid);
        } else {
            return this.getOddById(id, specialBetValue);
        }
    }

    getOddsMap(): Record<string, Odd> {
        return _keyBy(this.getOdds(), 'uniqueId');
    }

    public isStopped(): boolean {
        return _get(this.offerStatus, this.getActiveOffer()) === OfferStatus.stop;
    }

    public isOngoing(): boolean {
        return _includes(
            [OfferStatus.active, OfferStatus.block],
            _get(this.offerStatus, OfferType.live),
        );
    }

    public get isLocked(): boolean {
        return _get(this.offerStatus, this.getActiveOffer()) === OfferStatus.block;
    }

    public hasPreMatchBetting(): boolean {
        return _get(this.offerStatus, OfferType.preEvent) === OfferStatus.active
            && this.isInFuture();
    }

    public isViableForBetting(): boolean {
        return (_get(this.offerStatus, OfferType.preEvent) === OfferStatus.active || _get(this.offerStatus, OfferType.live) === OfferStatus.active)
            && this.isInFuture();
    }

    public isInFuture(): boolean {
        return !!this.date && isAfterDate(this.date, new Date());
    }

    public isFinished(): boolean {
        return !this.hasActiveOffer();
    }

    public hasActiveOffer(): boolean {
        return !!_find(this.offerStatus, (status) => {
            return status === OfferStatus.active || status === OfferStatus.block;
        });
    }

    public getActiveOffer(): OfferType {
        if (this.isOngoing()) {
            return OfferType.live;
        }

        return OfferType.preEvent;
    }

    public setFrozenOdds(odds: Odd[]): void {
        if (this.useOddResults()) {
            this.oddResults = odds;
        } else {
            this.odds = odds;
        }
    }

    public setOdds(odds: Odd[]): void {
        this.odds = odds;
        this.oddsUpdatedAt = Date.now();
        // Only reason to recompute markets
        // is change in odds
        this.marketsCache.clear();
    }

    public setOddResults(odds: Odd[]): void {
        this.oddResults = odds;
    }

    get liveData(): MetaData | null {
        if (!(this.metaData && this.metaData.periods)) {
            return null;
        }

        return this.metaData;
    }

    get isGoingToHaveLiveVideo(): boolean {
        return this.hasStream;
    }

    private hasMatchTag(tag: string): boolean {
        return this.matchTags.includes(tag);
    }

    get isSuperLiveEvent(): boolean {
        return this.hasMatchTag(superLiveTag);
    }
}

export function getMarketBy(odds: Odd[], predicate: (odd: Odd) => boolean): Market | null {
    const matchingOdds = odds.filter(predicate);
    if (matchingOdds.length === 0) {
        return null;
    }

    const [first, ...rest] = matchingOdds;
    const market = createMarket(first);
    // There can be 2 preselected markets, so we want to make sure the odds dont get mixed together,
    // But only show the odds for the first available market
    market.odds.push(...rest.filter((odd) => odd.marketId === first.marketId));
    market.isLocked = market.odds.every((odd) => odd.isLocked);
    return market;
}

export function getNameParts(fullName: string, delimiter = competitorDelimiter): [string, string] {
    if (!fullName) {
        return [
            '',
            '',
        ];
    }
    const parts = fullName.split(delimiter);
    if (parts.length < 2) {
        return [
            fullName,
            '',
        ];
    }
    return [
        parts[0],
        parts[1],
    ];
}
